// apps/widget/src/lib/api.ts
//
// The widget's API client. The publishable key rides on every request; the
// server checks it against the Origin allowlist, which is what actually
// protects the tenant.

// The widget's own setting first: in development the widget (opened on a
// phone) reaches the API through a public tunnel, while the dashboard keeps
// using localhost — its sign-in cookie is not sent to another site.
const API_BASE =
  import.meta.env['VITE_WIDGET_API_URL'] ?? import.meta.env['VITE_API_URL'] ?? 'https://api.skinvizo.com/v1';

export interface ApiError {
  code: string;
  message: string;
  details?: Record<string, unknown>;
  requestId?: string;
}

export class WidgetApiError extends Error {
  constructor(readonly error: ApiError) {
    super(error.message);
    this.name = 'WidgetApiError';
  }
}

let publishableKey = '';

export function setKey(key: string): void {
  publishableKey = key;
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const url = new URL(`${API_BASE}${path}`);
  url.searchParams.set('key', publishableKey);

  const response = await fetch(url, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...init.headers,
    },
    // The Origin header is what the server checks, and the browser sets it. No
    // credentials — the visitor is anonymous until they hand over details.
    credentials: 'omit',
  });

  if (!response.ok) {
    const body = (await response.json().catch(() => null)) as
      | { error?: ApiError; requestId?: string }
      | null;
    throw new WidgetApiError(
      body?.error ?? { code: 'UNKNOWN', message: 'Something went wrong. Please try again.' },
    );
  }

  return response.json() as Promise<T>;
}

export interface WidgetConfig {
  tenant: {
    name: string;
    logoUrl: string | null;
    brandColor: string | null;
    privacyPolicyUrl: string | null;
    country?: string | null;
  };
  mode: 'scan' | 'lead_only';
  isTestMode: boolean;
  enabledMetrics: string[];
  theme: {
    primaryColor: string;
    radius: number;
    font: string | null;
    /** Null: highlights follow the brand colour. Optional for configs cached before it existed. */
    accentColor?: string | null;
    /** Null: black or white, whichever reads on the brand colour. */
    buttonTextColor?: string | null;
    backgroundStyle?: 'plain' | 'tinted';
  };
  copy: {
    headline: string | null;
    /** Opening-screen overrides; null keeps the translated wording. */
    subtitle?: string | null;
    startLabel?: string | null;
    ctaLabel: string;
    disclaimer: string;
  };
  locales: string[];
  defaultLocale: string;
  showPoweredBy: boolean;
  simulationEnabled: boolean;
  heroImageUrl: string | null;
  urls: {
    widgetBase: string;
    allowedOrigins: string[];
    /** Dashboard origins whose live preview may restyle a ?preview=1 frame. */
    previewOrigins?: string[];
  };
  /** Whether the clinic offers the separate "keep my photo" box, and for how long. */
  photoRetention?: { offered: boolean; days: number };
}

/** The face as captured, for drawing regions on a kept photo. */
export interface FaceGeometry {
  width: number;
  height: number;
  landmarks: { x: number; y: number }[];
}

/** The staff report: the patient's report, plus their photo when it was kept. */
export interface StaffView {
  report: Report;
  face: (FaceGeometry & { photoUrl: string; keptUntil: string }) | null;
}

export interface ScanSession {
  sessionId: string;
  expiresAt: string;
  isTestMode: boolean;
  qrUrl: string;
  qrDataUrl: string;
  handoffToken: string;
}

export interface ReportMetric {
  key: string;
  label: string;
  score: number;
  band: 'fair' | 'average' | 'good';
  severity: number;
  regions: string[];
  advice?: string;
}

export interface Report {
  scanId: string;
  shortId: string;
  createdAt: string;
  isTestMode: boolean;
  skinScore: number;
  skinAge: number;
  skinAgeEstimated: boolean;
  skinType: string;
  fitzpatrick: number | null;
  band: 'fair' | 'average' | 'good';
  confidence: number;
  metrics: ReportMetric[];
  narrative: string | null;
  anatomyFlags: { key: string; label: string; present: boolean }[];
  disclaimer: string;
}

/** The intake answers as the API takes them; omitted keys were not answered. */
export interface IntakeAnswers {
  declaredAge?: number;
  dob?: string;
  pregnant?: boolean;
  onRetinoids?: boolean;
  goal?: string;
}

export const api = {
  config: () => request<WidgetConfig>('/public/config'),

  // The consent travels with the session: the server refuses a session without
  // it, and holds it there because the photo may be taken on another device.
  createSession: (declaredAge: number | undefined, consent: { retainPhoto: boolean }) =>
    request<ScanSession>('/public/scan-sessions', {
      method: 'POST',
      body: JSON.stringify({ declaredAge, consent: { biometric: true, retainPhoto: consent.retainPhoto } }),
    }),

  join: (sessionId: string, credentials: { token?: string; deviceToken?: string }) =>
    request<{ joined: boolean; retainPhoto?: boolean; deviceToken?: string }>(
      `/public/scan-sessions/${sessionId}/join`,
      { method: 'POST', body: JSON.stringify(credentials) },
    ),

  uploadUrl: (sessionId: string, contentType: string) =>
    request<{ uploadUrl: string; imageKey: string }>(
      `/public/scan-sessions/${sessionId}/upload-url`,
      { method: 'POST', body: JSON.stringify({ contentType }) },
    ),

  analyze: (
    sessionId: string,
    body: {
      imageKey: string;
      declaredAge?: number;
      faceBox?: { x: number; y: number; width: number; height: number };
      /** Sent only when this visitor agreed to keep their photo. */
      face?: FaceGeometry;
    },
  ) =>
    request<{ scanId: string; shortId: string }>(`/public/scan-sessions/${sessionId}/analyze`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  report: (scanId: string) => request<Report>(`/public/scans/${scanId}/report`),

  /** Clinic staff opening this report from their dashboard. */
  staffView: (token: string) => request<StaffView>(`/public/staff-view?token=${encodeURIComponent(token)}`),

  recommendations: (scanId: string) =>
    request<{ treatments: unknown[]; routine: { am: unknown[]; pm: unknown[] } }>(
      `/public/scans/${scanId}/recommendations`,
    ),

  lead: (body: {
    scanId?: string;
    firstName?: string;
    email?: string;
    phone?: string;
    marketingOptIn: boolean;
    intake?: IntakeAnswers;
    /** The widget's language, for the report email. */
    locale?: string;
  }) => request<{ ok: boolean; leadId?: string; emailed?: boolean }>('/public/scans/lead', {
    method: 'POST',
    body: JSON.stringify(body),
  }),

  bookingCta: (scanId: string, treatmentId?: string) =>
    request<{ mode: 'redirect' | 'embed' | 'callback'; url?: string }>('/public/booking/cta', {
      method: 'POST',
      body: JSON.stringify({ scanId, treatmentId }),
    }),

  callbackRequest: (body: {
    scanId?: string;
    name: string;
    contact: string;
    channel: 'phone' | 'whatsapp' | 'email';
    preferredDayPart: 'morning' | 'afternoon' | 'evening' | 'any';
    message?: string;
    intake?: IntakeAnswers;
  }) => request<{ ok: boolean }>('/public/booking/callback-requests', {
    method: 'POST',
    body: JSON.stringify(body),
  }),
};
